import React, { FC, useEffect, useState } from "react";
import { IoSearchSharp } from "react-icons/io5";
import { BsCheckCircleFill } from "react-icons/bs";
import classes from "./style.module.scss";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { TCropOption } from "../../../types";
import { useAllCropsList } from "../../../hooks/useAllCrops";
import { Button } from "../../ui";
import { FaAngleRight } from "react-icons/fa6";

const MultiCropSelection: FC<{
  selectionMode: "single" | "multiple";
  onSelect: (crops: TCropOption[]) => void;
  selectedCropsData: TCropOption[];
  disableExistingSelections?: boolean;
}> = ({
  selectionMode,
  onSelect,
  selectedCropsData,
  disableExistingSelections = false,
}) => {
  const { crops } = useAllCropsList();
  const [selectedCrops, setSelectedCrops] = useState<{
    [key: number]: TCropOption;
  }>({});

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (selectedCropsData?.length) {
      const activeIncomingCrops = selectedCropsData.filter(
        (crop: any) => crop.status !== 0
      );

      const preselected = Object.fromEntries(
        activeIncomingCrops.map((crop) => [crop.cropId, crop])
      );
      setSelectedCrops(preselected);
    } else {
      setSelectedCrops({});
    }
  }, [selectedCropsData]);

  const onSelectCrop = (crop: TCropOption) => {
    if (disableExistingSelections) {
      const isAlreadyActive = selectedCropsData?.some(
        (c: any) => c.cropId === crop.cropId && c.status !== 0
      );
      if (isAlreadyActive) return;
    }
    setSelectedCrops((prev) => {
      const updated = { ...prev };
      if (updated[crop.cropId]) {
        delete updated[crop.cropId];
      } else {
        updated[crop.cropId] = crop;
      }
      return updated;
    });
  };

  const clearSearch = () => {
    setSearchText("");
  };

  return (
    <>
      <div>
        <div className="relative">
          <input
            style={{
              textIndent: ".9rem",
              color: "var(--ono-subtext)",
            }}
            placeholder="Search crop"
            value={searchText}
            onChange={(e: any) => {
              setSearchText(e.target.value);
            }}
            className="border br-10 px-3 py-2 w-full mb-3"
          />
          <IoSearchSharp
            className="absolute"
            style={{ top: "0.8rem", left: "0.5rem" }}
          />
          {searchText !== "" && (
            <IoMdCloseCircleOutline
              onClick={() => {
                clearSearch();
              }}
              className="absolute color-primary cursor-pointer"
              style={{
                top: ".7rem",
                right: "1rem",
                fontSize: "1.2rem",
                color: "gray",
                cursor: "pointer",
              }}
            />
          )}
        </div>
      </div>
      <div className="h-80 overflow-auto grid grid-cols-4 gap-2 gap-y-3 ">
        {(searchText
          ? crops.filter((crop) =>
              crop.cropName.toLowerCase().includes(searchText.toLowerCase())
            )
          : crops
        ).map((crop) => {
          const isAlreadyActive = selectedCropsData?.some(
            (c: any) => c.cropId === crop.cropId && c.status !== 0
          );

          const shouldDisable = disableExistingSelections && isAlreadyActive;
          const isCurrentCropSelected = !!selectedCrops[crop.cropId];

          return (
            <span
              key={crop.cropId}
              className={`flex items-center flex-col text-center relative ${
                isCurrentCropSelected ? classes.selected : ""
              }`}
              style={{
                opacity: shouldDisable ? 0.4 : 1,
                cursor: shouldDisable ? "not-allowed" : "pointer",
                pointerEvents: shouldDisable ? "none" : "auto",
              }}
              onClick={() => {
                onSelectCrop(crop);
              }}
            >
              <img
                alt="crop_image"
                height={40}
                src={crop.imageUrl}
                width={40}
              />
              <span className="text-xs font-semibold">{crop.cropName}</span>
              <BsCheckCircleFill className={classes.selectedIcon} />
            </span>
          );
        })}
      </div>
      {selectionMode === "multiple" && (
        <div className="flex px-4 py-2">
          <Button
            className="ml-auto"
            disabled={Object.keys(selectedCrops).length === 0}
            onClick={() => {
              onSelect(Object.values(selectedCrops));
              setSelectedCrops({});
            }}
          >
            Continue&nbsp;&nbsp;
            <FaAngleRight />
          </Button>
        </div>
      )}
    </>
  );
};

export default MultiCropSelection;
